import type { ICellEditorComp, ICellEditorParams } from "ag-grid-enterprise";

/**
 * AG Grid cell editor that mounts a registered custom HTML element. The
 * element receives initial `value` and `row` via property assignment, so
 * it must expose setters for whichever it cares about.
 *
 * Wired in via `cellEditor: CustomElementCellEditor` and
 * `cellEditorParams: { tagName, attributes? }`.
 *
 * `attributes` is a string-to-string map applied once at init. These come
 * from the backend-driven column def and are static for the editor's
 * lifetime.
 *
 * Commit contract: the element dispatches a bubbling `change` event when
 * the user is done; the editor calls `stopEditing()` and AG Grid reads
 * `el.value` back via `getValue()`. Compound editors with multiple inner
 * inputs typically don't dispatch `change` per keystroke — they rely on
 * `stopEditingWhenCellsLoseFocus` to drive commit and just keep `el.value`
 * accurate.
 *
 * Focus contract: on edit-start the editor calls `el.focus()` and lets the
 * element decide what that means. Custom elements aren't focusable by
 * default, so the element must either implement its own `focus()` method
 * (forwarding to the right inner control) or set `tabindex` on itself.
 * Without one of those, focus silently falls back to `<body>` and keyboard
 * editing won't work.
 */
export class CustomElementCellEditor implements ICellEditorComp {
  private el!: HTMLElement & { value?: unknown; row?: unknown };
  private onChange!: (event: Event) => void;

  init(params: ICellEditorParams): void {
    const { tagName, attributes } = params as ICellEditorParams & {
      tagName: string;
      attributes?: Record<string, string>;
    };
    if (!customElements.get(tagName)) {
      console.warn(
        `[DATA GRID] cellEditorElement "${tagName}" is not a registered custom element. ` +
          `Import the file that calls customElements.define("${tagName}", …) before the grid mounts.`
      );
    }
    this.el = document.createElement(tagName);

    if (attributes) {
      for (const [name, value] of Object.entries(attributes)) {
        this.el.setAttribute(name, String(value));
      }
    }

    this.el.value = params.value;
    this.el.row = params.data;

    this.onChange = () => params.stopEditing();
    this.el.addEventListener("change", this.onChange);
  }

  getGui(): HTMLElement {
    return this.el;
  }

  getValue(): unknown {
    return this.el.value;
  }

  afterGuiAttached(): void {
    this.el.focus();
  }

  destroy(): void {
    this.el.removeEventListener("change", this.onChange);
  }
}
